import { Link } from "react-router-dom"

export default function SMSCompliancePage() {
  const currentYear = new Date().getFullYear()

  return (
    <article
      style={{
        maxWidth: 800,
        width: "100%",
        margin: "0 auto",
        padding: "clamp(24px, 6vw, 48px) clamp(20px, 4vw, 32px)",
        display: "grid",
        gap: "clamp(24px, 4vw, 36px)",
      }}
    >
      <header>
        <h1
          className="mono"
          style={{
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            fontSize: "clamp(1.5rem, 4vw, 2rem)",
            marginBottom: "12px",
          }}
        >
          SMS Message Flow &amp; Compliance
        </h1>
        <p className="small" style={{ color: "var(--muted)" }}>
          Last Updated: January {currentYear}
        </p>
      </header>

      <section>
        <h2
          className="mono"
          style={{
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            fontSize: "1.1rem",
            marginBottom: "16px",
            color: "var(--accent-text)",
          }}
        >
          1. Program Overview
        </h2>
        <p style={{ color: "var(--text)", lineHeight: 1.7, marginBottom: "12px" }}>
          <strong>Sales Demon</strong> is an AI-powered back office assistant that helps small
          businesses manage invoicing, payment collection, and customer communication entirely
          through SMS text messages.
        </p>
        <p style={{ color: "var(--muted)", lineHeight: 1.7 }}>
          Users text commands to their dedicated Sales Demon phone number to create invoices,
          track payments, and receive automated notifications about their business operations.
          No app download required.
        </p>
      </section>

      <section>
        <h2
          className="mono"
          style={{
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            fontSize: "1.1rem",
            marginBottom: "16px",
            color: "var(--accent-text)",
          }}
        >
          2. How Users Opt-In (Consent Collection)
        </h2>
        <p style={{ color: "var(--text)", lineHeight: 1.7, marginBottom: "16px" }}>
          Users provide explicit consent to receive SMS messages during our web-based onboarding
          process at{" "}
          <a href="https://salesdemon.ai/onboard" style={{ color: "var(--accent-text)" }}>
            salesdemon.ai/onboard
          </a>{" "}
          (accessible from the Get Started button on the homepage). The opt-in flow works as
          follows:
        </p>

        <div
          style={{
            background: "rgba(255, 255, 255, 0.03)",
            border: "1px solid var(--rail)",
            padding: "20px",
            marginBottom: "20px",
          }}
        >
          <h3
            className="mono small"
            style={{
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              marginBottom: "16px",
              color: "var(--text)",
            }}
          >
            Step-by-Step Opt-In Process
          </h3>
          <ol style={{ color: "var(--muted)", lineHeight: 1.8, paddingLeft: "20px" }}>
            <li style={{ marginBottom: "12px" }}>
              <strong style={{ color: "var(--text)" }}>Phone Number Entry:</strong> User enters
              their mobile phone number on the onboarding page.
            </li>
            <li style={{ marginBottom: "12px" }}>
              <strong style={{ color: "var(--text)" }}>SMS Consent Checkbox:</strong> User must
              check a clearly visible checkbox that states:{" "}
              <em>
                &ldquo;I agree to receive SMS notifications from Sales Demon about my account,
                invoices, payments, and business operations.&rdquo;
              </em>
            </li>
            <li style={{ marginBottom: "12px" }}>
              <strong style={{ color: "var(--text)" }}>Disclosure Text:</strong> Below the
              checkbox, users see: &ldquo;Message frequency varies based on account activity. Message
              and data rates may apply. Reply STOP to unsubscribe or HELP for help.&rdquo;
            </li>
            <li style={{ marginBottom: "12px" }}>
              <strong style={{ color: "var(--text)" }}>Phone Verification:</strong> User receives
              a 6-digit verification code via SMS and enters it to confirm ownership.
            </li>
            <li>
              <strong style={{ color: "var(--text)" }}>Opt-In Confirmation:</strong> Upon
              successful verification, user receives a confirmation message (see Section 3).
            </li>
          </ol>
        </div>

        <div
          style={{
            border: "1px solid var(--rail)",
            padding: "16px",
            marginBottom: "20px",
            background: "rgba(255, 255, 255, 0.02)",
          }}
        >
          <p
            className="mono small"
            style={{
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              marginBottom: "12px",
              color: "var(--muted)",
            }}
          >
            Opt-In Screenshot
          </p>
          <a href="/assets/legal/sms-opt-in.png" target="_blank" rel="noreferrer">
            <img
              src="/assets/legal/sms-opt-in.png"
              alt="Sales Demon onboarding screen showing the required SMS consent checkbox"
              loading="lazy"
              style={{
                width: "100%",
                border: "1px solid var(--rail)",
                borderRadius: "8px",
              }}
            />
          </a>
          <p style={{ color: "var(--muted)", lineHeight: 1.6, marginTop: "12px" }}>
            Screenshot of the onboarding step where users enter their phone number and check the
            required SMS consent box.
          </p>
        </div>

        <p style={{ color: "var(--muted)", lineHeight: 1.7 }}>
          The SMS consent checkbox is <strong>required</strong> and cannot be bypassed. Users
          cannot complete onboarding without explicitly agreeing to receive SMS messages.
        </p>
      </section>

      <section>
        <h2
          className="mono"
          style={{
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            fontSize: "1.1rem",
            marginBottom: "16px",
            color: "var(--accent-text)",
          }}
        >
          3. Opt-In Confirmation Message
        </h2>
        <p style={{ color: "var(--text)", lineHeight: 1.7, marginBottom: "16px" }}>
          Immediately after a user completes phone verification and opt-in, they receive the
          following confirmation message:
        </p>
        <div
          style={{
            background: "rgba(255, 43, 79, 0.08)",
            border: "1px solid rgba(255, 43, 79, 0.25)",
            padding: "20px",
            fontFamily: "monospace",
            fontSize: "0.95rem",
            lineHeight: 1.6,
            color: "var(--text)",
          }}
        >
          &ldquo;Welcome to Sales Demon! You&apos;re now set up to receive SMS notifications about your
          invoices, payments, and account activity. Message frequency varies. Msg &amp; data
          rates may apply. Reply HELP for support or STOP to unsubscribe.&rdquo;
        </div>
      </section>

      <section>
        <h2
          className="mono"
          style={{
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            fontSize: "1.1rem",
            marginBottom: "16px",
            color: "var(--accent-text)",
          }}
        >
          4. Types of Messages Sent
        </h2>
        <p style={{ color: "var(--text)", lineHeight: 1.7, marginBottom: "16px" }}>
          After opting in, users may receive the following types of SMS messages:
        </p>
        <ul style={{ color: "var(--muted)", lineHeight: 1.8, paddingLeft: "20px" }}>
          <li style={{ marginBottom: "8px" }}>
            <strong style={{ color: "var(--text)" }}>Invoice Confirmations:</strong> When user
            creates and sends an invoice via text command
          </li>
          <li style={{ marginBottom: "8px" }}>
            <strong style={{ color: "var(--text)" }}>Payment Notifications:</strong> When a
            customer pays an invoice
          </li>
          <li style={{ marginBottom: "8px" }}>
            <strong style={{ color: "var(--text)" }}>Payment Reminders:</strong> Automated
            reminders for upcoming or overdue invoices
          </li>
          <li style={{ marginBottom: "8px" }}>
            <strong style={{ color: "var(--text)" }}>Account Alerts:</strong> Important updates
            about account status, Stripe connection, etc.
          </li>
          <li>
            <strong style={{ color: "var(--text)" }}>Responses to Commands:</strong> Replies to
            user-initiated text commands
          </li>
        </ul>
      </section>

      <section>
        <h2
          className="mono"
          style={{
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            fontSize: "1.1rem",
            marginBottom: "16px",
            color: "var(--accent-text)",
          }}
        >
          5. Message Frequency
        </h2>
        <p style={{ color: "var(--text)", lineHeight: 1.7 }}>
          Message frequency varies based on user activity. Typical users receive{" "}
          <strong>5-20 messages per week</strong> depending on their invoicing volume, customer
          payment activity, and usage of text commands. Users who send more invoices or have
          more active customers will receive more notifications.
        </p>
      </section>

      <section>
        <h2
          className="mono"
          style={{
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            fontSize: "1.1rem",
            marginBottom: "16px",
            color: "var(--accent-text)",
          }}
        >
          6. Sample Messages
        </h2>
        <div style={{ display: "grid", gap: "12px" }}>
          <div
            style={{
              background: "rgba(255, 255, 255, 0.03)",
              border: "1px solid var(--rail)",
              padding: "16px",
            }}
          >
            <p
              className="mono small"
              style={{ color: "var(--muted)", marginBottom: "8px", letterSpacing: "0.08em" }}
            >
              Invoice Sent
            </p>
            <p style={{ color: "var(--text)", fontFamily: "monospace", fontSize: "0.9rem" }}>
              &ldquo;Sales Demon: Invoice #[143] for $[300] sent to [Gary]. I&apos;ll notify you when
              payment is received. Reply STOP to opt out.&rdquo;
            </p>
          </div>
          <div
            style={{
              background: "rgba(255, 255, 255, 0.03)",
              border: "1px solid var(--rail)",
              padding: "16px",
            }}
          >
            <p
              className="mono small"
              style={{ color: "var(--muted)", marginBottom: "8px", letterSpacing: "0.08em" }}
            >
              Payment Received
            </p>
            <p style={{ color: "var(--text)", fontFamily: "monospace", fontSize: "0.9rem" }}>
              &ldquo;Sales Demon: [Gary] paid invoice #[143]. $[300] will arrive in your account
              within 1-2 business days. Reply HELP for support.&rdquo;
            </p>
          </div>
          <div
            style={{
              background: "rgba(255, 255, 255, 0.03)",
              border: "1px solid var(--rail)",
              padding: "16px",
            }}
          >
            <p
              className="mono small"
              style={{ color: "var(--muted)", marginBottom: "8px", letterSpacing: "0.08em" }}
            >
              Payment Reminder
            </p>
            <p style={{ color: "var(--text)", fontFamily: "monospace", fontSize: "0.9rem" }}>
              &ldquo;Sales Demon: Reminder - Invoice #[156] for [Sarah] ($[500]) is [7] days overdue.
              Reply &apos;remind [Sarah]&apos; to send a follow-up. Reply STOP to unsubscribe.&rdquo;
            </p>
          </div>
        </div>
        <p
          className="small"
          style={{ color: "var(--muted)", marginTop: "12px", fontStyle: "italic" }}
        >
          Note: [Bracketed] values indicate templated fields that vary per message.
        </p>
      </section>

      <section>
        <h2
          className="mono"
          style={{
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            fontSize: "1.1rem",
            marginBottom: "16px",
            color: "var(--accent-text)",
          }}
        >
          7. How to Opt-Out (STOP)
        </h2>
        <p style={{ color: "var(--text)", lineHeight: 1.7, marginBottom: "16px" }}>
          Users can opt out of SMS messages at any time using these methods:
        </p>
        <ul style={{ color: "var(--muted)", lineHeight: 1.8, paddingLeft: "20px" }}>
          <li style={{ marginBottom: "8px" }}>
            <strong style={{ color: "var(--text)" }}>Reply STOP:</strong> Text &ldquo;STOP&rdquo; to any
            Sales Demon message
          </li>
          <li style={{ marginBottom: "8px" }}>
            <strong style={{ color: "var(--text)" }}>Alternative Keywords:</strong> UNSUBSCRIBE,
            CANCEL, END, QUIT also work
          </li>
          <li>
            <strong style={{ color: "var(--text)" }}>Contact Support:</strong> Email
            support@salesdemon.ai to request removal
          </li>
        </ul>

        <h3
          className="mono small"
          style={{
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            marginTop: "20px",
            marginBottom: "12px",
            color: "var(--text)",
          }}
        >
          Opt-Out Confirmation Message
        </h3>
        <div
          style={{
            background: "rgba(255, 43, 79, 0.08)",
            border: "1px solid rgba(255, 43, 79, 0.25)",
            padding: "20px",
            fontFamily: "monospace",
            fontSize: "0.95rem",
            lineHeight: 1.6,
            color: "var(--text)",
          }}
        >
          &ldquo;Sales Demon: You&apos;ve been unsubscribed and will no longer receive messages from us.
          Reply START to re-subscribe. Questions? Email support@salesdemon.ai&rdquo;
        </div>
      </section>

      <section>
        <h2
          className="mono"
          style={{
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            fontSize: "1.1rem",
            marginBottom: "16px",
            color: "var(--accent-text)",
          }}
        >
          8. How to Get Help (HELP)
        </h2>
        <p style={{ color: "var(--text)", lineHeight: 1.7, marginBottom: "16px" }}>
          Users can request help at any time:
        </p>
        <ul style={{ color: "var(--muted)", lineHeight: 1.8, paddingLeft: "20px" }}>
          <li style={{ marginBottom: "8px" }}>
            <strong style={{ color: "var(--text)" }}>Reply HELP:</strong> Text &ldquo;HELP&rdquo; to any
            Sales Demon number
          </li>
          <li style={{ marginBottom: "8px" }}>
            <strong style={{ color: "var(--text)" }}>Alternative Keywords:</strong> INFO,
            SUPPORT also work
          </li>
          <li>
            <strong style={{ color: "var(--text)" }}>Email:</strong> support@salesdemon.ai
          </li>
        </ul>

        <h3
          className="mono small"
          style={{
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            marginTop: "20px",
            marginBottom: "12px",
            color: "var(--text)",
          }}
        >
          Help Response Message
        </h3>
        <div
          style={{
            background: "rgba(255, 43, 79, 0.08)",
            border: "1px solid rgba(255, 43, 79, 0.25)",
            padding: "20px",
            fontFamily: "monospace",
            fontSize: "0.95rem",
            lineHeight: 1.6,
            color: "var(--text)",
          }}
        >
          &ldquo;Sales Demon Help: For support, email support@salesdemon.ai or visit
          salesdemon.ai/help. To stop messages, reply STOP. Msg &amp; data rates may apply.&rdquo;
        </div>
      </section>

      <section>
        <h2
          className="mono"
          style={{
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            fontSize: "1.1rem",
            marginBottom: "16px",
            color: "var(--accent-text)",
          }}
        >
          9. Supported Carriers
        </h2>
        <p style={{ color: "var(--text)", lineHeight: 1.7 }}>
          Sales Demon SMS is supported on all major U.S. carriers including AT&amp;T, Verizon,
          T-Mobile, US Cellular, and others. Message and data rates may apply based on your
          carrier plan.
        </p>
      </section>

      <section>
        <h2
          className="mono"
          style={{
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            fontSize: "1.1rem",
            marginBottom: "16px",
            color: "var(--accent-text)",
          }}
        >
          10. Privacy &amp; Data Handling
        </h2>
        <p style={{ color: "var(--text)", lineHeight: 1.7, marginBottom: "12px" }}>
          We take your privacy seriously:
        </p>
        <ul style={{ color: "var(--muted)", lineHeight: 1.8, paddingLeft: "20px" }}>
          <li style={{ marginBottom: "8px" }}>
            We do <strong style={{ color: "var(--text)" }}>not</strong> sell or share your phone
            number with third parties for marketing purposes
          </li>
          <li style={{ marginBottom: "8px" }}>
            Phone numbers are used solely to deliver Sales Demon service messages
          </li>
          <li style={{ marginBottom: "8px" }}>
            SMS opt-in data and consent is not shared with third parties
          </li>
          <li>
            See our full{" "}
            <Link to="/legal/privacy" style={{ color: "var(--accent-text)" }}>
              Privacy Policy
            </Link>{" "}
            for details
          </li>
        </ul>
      </section>

      <section>
        <h2
          className="mono"
          style={{
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            fontSize: "1.1rem",
            marginBottom: "16px",
            color: "var(--accent-text)",
          }}
        >
          11. Contact Information
        </h2>
        <p style={{ color: "var(--text)", lineHeight: 1.7 }}>
          <strong>Sales Demon</strong>
          <br />
          Operated by Demons and Fairies Inc.
          <br />
          Email: support@salesdemon.ai
          <br />
          Web:{" "}
          <a href="https://salesdemon.ai" style={{ color: "var(--accent-text)" }}>
            salesdemon.ai
          </a>
        </p>
      </section>

      <footer
        style={{
          marginTop: "24px",
          paddingTop: "24px",
          borderTop: "1px solid var(--rail)",
          display: "flex",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <Link
          to="/legal/sms-terms"
          className="mono small"
          style={{ color: "var(--accent-text)", letterSpacing: "0.08em" }}
        >
          SMS Terms of Service →
        </Link>
        <Link
          to="/legal/privacy"
          className="mono small"
          style={{ color: "var(--accent-text)", letterSpacing: "0.08em" }}
        >
          Privacy Policy →
        </Link>
        <Link
          to="/legal/terms"
          className="mono small"
          style={{ color: "var(--accent-text)", letterSpacing: "0.08em" }}
        >
          Terms of Service →
        </Link>
        <Link
          to="/"
          className="mono small"
          style={{ color: "var(--muted)", letterSpacing: "0.08em" }}
        >
          ← Back to Home
        </Link>
      </footer>
    </article>
  )
}
